import type * as React from "react";

import { LoginBlob } from "../blob";
import { cn } from "../../lib/cn";

/**
 * StreamingIndicator — SOTA May 2026 "AI is thinking" shimmer text.
 *
 * Pattern: a low-contrast inline label with a single orange specular sweep
 * traversing left → right ~every 1.8s. No bubble, no border, no shadow,
 * no shape-shifter blob — the indicator is purely typographic.
 *
 * Why shimmer-text (vs blob / bubble / dancing dots):
 *   - Verified May 2026 SOTA pattern (`assistant-ui` `tw-shimmer`, Vercel
 *     AI SDK, Cursor, Linear AI). The 2026 trend is "end of visual
 *     theatrics" — readers parse motion-on-text as "active processing"
 *     instead of "stuck UI".
 *   - Zero chrome means it slots inline with the conversation typography:
 *     no z-index war with the surrounding bubbles, no scroll jank from a
 *     fat container appearing/disappearing.
 *   - The label is dynamic ("Sto leggendo le email…" / "Cerco nei
 *     documenti…" / "Sto componendo la risposta…"): readers learn the
 *     agent state without needing the full ThinkingChain card to mount.
 *
 *   • 0–300ms   → render nothing (avoids flash on near-instant replies)
 *   • >300ms    → shimmer text + tiny orange anchor dot
 *
 * Accessibility:
 *   - `<output>` with `role="status"` (implicit) + `aria-live="polite"`
 *   - `sr-only` "Ando sta elaborando" — SRs read once on mount, ignore
 *     the gradient animation entirely
 *   - All animations wrapped in `motion-safe:` (reduced-motion friendly):
 *     `prefers-reduced-motion: reduce` → opacity-only pulse, no sweep
 *
 * Label resolution (the v1 contract used by `route.tsx`):
 *   1. `reasoningHopLabel` (live label from the latest `reasoning_hop`)
 *   2. fallback "Sto elaborando…"
 */
export interface StreamingIndicatorProps {
  /**
   * Milliseconds elapsed since the streaming flag flipped to true. When
   * undefined (e.g. SSR / first render before the hook ticks), we render
   * the indicator immediately — no zero-flash.
   */
  elapsedMs?: number;
  /**
   * Live label derived from the most recent `reasoning_hop`. When unset,
   * we fall back to a generic "Sto elaborando…" (always present so the
   * indicator never reads as a stripped/broken UI).
   */
  reasoningHopLabel?: string | null;
  /** Extra classes for the outer wrapper (alignment / spacing). */
  className?: string;
}

const REVEAL_MS = 300;
const DEEP_THINK_MS = 5000;

export const StreamingIndicator: React.FC<StreamingIndicatorProps> = ({
  elapsedMs,
  reasoningHopLabel,
  className,
}) => {
  // Hide for the first 300ms so instant replies don't flash.
  if (typeof elapsedMs === "number" && elapsedMs < REVEAL_MS) {
    return null;
  }

  const trimmed = reasoningHopLabel?.trim();
  const label =
    trimmed && trimmed.length > 0
      ? trimmed
      : typeof elapsedMs === "number" && elapsedMs > DEEP_THINK_MS
        ? "Sto pensando…"
        : "Sto elaborando…";

  return (
    <output
      aria-live="polite"
      aria-busy="true"
      className={cn(
        "inline-flex items-center gap-2.5 px-1 py-1 motion-safe:animate-msg-in",
        className,
      )}
    >
      {/* Brand anchor — live WebGL Ando blob (32px). Same metaballs
          shader as the login hero + landing visual, just inline-scaled.
          Replaces the 6px orange dot pattern so the streaming indicator
          carries the brand-defining motion identity throughout the chat. */}
      <span
        aria-hidden
        className="block h-8 w-8 shrink-0"
      >
        <LoginBlob size="mini" />
      </span>
      <span className="shimmer-text font-medium text-[13px] leading-tight tracking-[-0.005em]">
        {label}
      </span>
      <span className="sr-only">Ando sta elaborando</span>
    </output>
  );
};
StreamingIndicator.displayName = "StreamingIndicator";
